import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

/* Bulgarian is bundled; English is fetched.
 *
 * Both locales used to be static imports, which put 177 kB of raw JSON — 49 kB gzipped — into the
 * main chunk, and every Bulgarian visitor downloaded the English copy they will never read. Bulgarian
 * has to stay bundled regardless: it is `fallbackLng`, so i18next needs it loaded whatever the user
 * picked, and it is the primary market, so bundling it is also the fast path for most people.
 *
 * English costs one extra request, taken before the first render rather than after it — see
 * `i18nReady`. A visitor who arrives on /en/* must not watch a Bulgarian page repaint into English. */
import bgCommon from './bg/common.json';
import { expandTerms } from '../lib/terms';
import { useTermsStore } from '../state/termsStore';

/**
 * Writes abbreviations out in full, for the reader who asked for that in settings.
 *
 * Done to the string as it leaves i18next rather than in the components, because that is the only
 * place it can be complete: two hundred call sites, and the ones that matter most are the ones a
 * component could not wrap anyway — a placeholder, an aria-label, an option in a select.
 *
 * The glossary is left alone. Its headwords are what a reader looks up, and „ДОО“ is the word they
 * saw on the portal and came here to find. So is the setting itself, which names the abbreviations
 * in order to talk about them.
 */
const TERMS_UNTOUCHED = ['help.entries', 'settings.terms'];

i18n.use({
  type: 'postProcessor',
  name: 'terms',
  process(value: unknown, key: string | string[], _options: unknown, translator: { language?: string }) {
    if (typeof value !== 'string' || useTermsStore.getState().mode !== 'full') {
      return value;
    }

    const name = Array.isArray(key) ? key[0] : key;

    if (TERMS_UNTOUCHED.some((prefix) => name === prefix || name.startsWith(`${prefix}.`))) {
      return value;
    }

    return expandTerms(value, translator.language ?? 'bg');
  },
});

export const SUPPORTED_LANGUAGES = ['bg', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_STORAGE_KEY = 'danak.language';

const init = i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      bg: { common: bgCommon },
    },
    // Bulgarian is the primary market; English is the deliberate second locale for relocated
    // freelancers, so an unrecognised browser language should land on Bulgarian.
    fallbackLng: 'bg',
    supportedLngs: SUPPORTED_LANGUAGES,
    defaultNS: 'common',
    detection: {
      // Path first, so /en/* renders English for everyone regardless of browser or stored
      // preference. The marketing routes are mirrored per language and each has its own
      // pre-rendered <head>; if the body followed a stored preference instead, the hreflang pairs
      // would advertise a Bulgarian page that renders in English. A first segment that is not a
      // language (/calculator, /dashboard) falls through to the stored preference, which is what
      // the signed-in app should keep using.
      //
      // `navigator` is deliberately absent. It used to sit at the end, which meant a Bulgarian
      // visitor on an English-language browser — ordinary here — had "/" detected as English and
      // saw the landing page render in English before an effect corrected it. Correcting it in an
      // effect is too late: the page has already painted, and only the components that re-render
      // pick up the change, so the first impression was an English body under a Bulgarian nav.
      // Resolving synchronously from path-then-preference removes the wrong first paint entirely,
      // and "/" being Bulgarian is exactly what its canonical URL and <head> already promise.
      order: ['path', 'localStorage'],
      lookupFromPathIndex: 0,
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    postProcess: ['terms'],
    // `termsChanged` is ours: see the subscription below.
    react: { bindI18n: 'languageChanged termsChanged' },
  });

// A string is post-processed when a component renders, so a changed setting means nothing until
// the components that called `t` render again. react-i18next already does that for a language
// change; this gives it a second event to do it for.
useTermsStore.subscribe((state, previous) => {
  if (state.mode !== previous.mode) {
    i18n.emit('termsChanged');
  }
});

// Keeps the document language in sync for screen readers and for Intl formatting defaults.
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

const loaded = new Set(['bg']);

/** Fetches a locale's bundle once, and does nothing for one that is already in memory. */
async function loadLocale(language: string): Promise<void> {
  if (loaded.has(language) || language !== 'en') {
    return;
  }

  const { default: bundle } = await import('./en/common.json');

  i18n.addResourceBundle('en', 'common', bundle, true, true);
  loaded.add('en');
}

/**
 * Change the UI language, fetching its bundle first.
 *
 * Every language switch has to go through here rather than through `i18n.changeLanguage`. Calling
 * that directly with a locale we have not fetched puts i18next into English with no English
 * strings, so it renders the Bulgarian fallback and never revisits it — a half-translated page with
 * nothing to trigger a repair.
 */
export async function setLanguage(language: string): Promise<void> {
  await loadLocale(language);
  await i18n.changeLanguage(language);
}

/**
 * Resolves once the detected language is actually usable.
 *
 * Detection is synchronous by design — the comment on `order` above explains why a wrong first paint
 * is worse than a slower one — but an English visitor now needs a fetch before that decision means
 * anything. `main.tsx` waits for this before rendering, so the trade is one request for English
 * visitors and none for Bulgarian ones, rather than a flash of the wrong language for everybody.
 */
export const i18nReady: Promise<void> = init.then(() => loadLocale(i18n.resolvedLanguage ?? 'bg'));

export default i18n;
